// Staff-facing minimum booking notice classification (OTC-384).
//
// Mirrors the customer-side cutoff semantics: enforcement lives in
// booking-site-make-booking-v2 validateBookingWindow, display classification in
// questgenie's slot feed (calculateOffsetMs, months approximated as 30 days).
// Any change to those semantics must be mirrored here and in questgenie.
//
// All math stays in wall-clock space: slot times are venue wall-clock and the
// anchor string is venue wall-clock, both parsed as browser-local Dates, so
// differences are consistent regardless of the viewer's timezone or clock.
import moment from 'moment-timezone'

export const NOTICE_TICK_MS = 30000

const COUNTDOWN_HORIZON_MS = 2 * 60 * 60 * 1000

export interface NoticeAnchor {
  serverNowMs: number
  fetchedAtMs: number
}

export interface SlotNoticeInput {
  status: string
  is_all_day?: number | boolean | null
  booking_date: string
  end_date?: string | null
  start_time: string
  end_time?: string | null
  min_booking_period?: number | null
  min_booking_value?: number | null
  allow_late_booking?: number | null
  hide_past_min_notice?: number | null
  request_past_min_notice?: number | null
}

export interface SlotNotice {
  state: 'closed' | 'countdown'
  // Short chip text. Kept terse because the list view's status column is
  // narrow; the clock icon carries the "time" meaning.
  label: string
  // Full sentence for the chip's title tooltip.
  title: string
}

export type NoticeDisplayMode = 'indicator' | 'countdown'

// Call once per feed response. serverNow is "YYYY-MM-DDTHH:mm:ss" venue
// wall-clock, or null when the venue has no usable timezone.
export function captureNoticeAnchor(serverNow: string | null | undefined): NoticeAnchor | null {
  if (!serverNow) return null
  const serverNowMs = Date.parse(serverNow)
  if (isNaN(serverNowMs)) return null
  return { serverNowMs, fetchedAtMs: Date.now() }
}

// Venue wall-clock "now" in ms: anchor plus elapsed time, else the group's
// configured timezone, else the device clock.
export function getVenueNowMs(anchor: NoticeAnchor | null): number {
  if (anchor) {
    return anchor.serverNowMs + (Date.now() - anchor.fetchedAtMs)
  }
  try {
    const raw = localStorage.getItem('group')
    const group = raw ? JSON.parse(raw) : null
    if (group && group.tz_name && moment.tz.zone(group.tz_name)) {
      const parsed = Date.parse(moment.tz(group.tz_name).format('YYYY-MM-DDTHH:mm:ss'))
      if (!isNaN(parsed)) return parsed
    }
  } catch (e) {
    // fall through to device clock
  }
  return Date.now()
}

// Same unit table as the customer-side slot feed: 0=minutes 1=hours 2=days
// 3=weeks 4=months (approximated as 30 days). Null/unknown gives 0.
function offsetMs(period: number | null | undefined, value: number | null | undefined): number {
  const v = Number(value) || 0
  if (v <= 0) return 0
  switch (period) {
    case 0: return v * 60 * 1000
    case 1: return v * 60 * 60 * 1000
    case 2: return v * 24 * 60 * 60 * 1000
    case 3: return v * 7 * 24 * 60 * 60 * 1000
    case 4: return v * 30 * 24 * 60 * 60 * 1000
    default: return 0
  }
}

function wallClockMs(dateValue: string, time: string): number {
  // booking_date/end_date are venue wall-clock. The mysql driver serializes
  // raw DATE columns as ISO UTC strings ("2026-08-17T00:00:00.000Z"), so
  // take the literal leading date; parsing it as a Date would shift it a day
  // in negative-UTC timezones and misplace the slot start.
  const s = String(dateValue)
  const literal = s.match(/^(\d{4}-\d{2}-\d{2})/)
  const dateStr = literal ? literal[1] : moment(s).format('YYYY-MM-DD')
  return Date.parse(`${dateStr}T${time}`)
}

function countdownRemaining(remainingMs: number): string {
  const totalMinutes = Math.max(1, Math.ceil(remainingMs / 60000))
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (hours > 0) {
    // "1h" rather than "1h 0m": the extra characters wrap on narrow cards.
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`
  }
  return `${minutes}m`
}

// What customers see on the booking site once the cutoff passes. Used as the
// first tooltip sentence so hovering answers "what is the customer seeing
// right now", which is the part staff cannot infer from the chip.
function customerEffect(slot: SlotNoticeInput): string {
  if (Number(slot.hide_past_min_notice) === 1) {
    return 'This slot is hidden on the booking site.'
  }
  if (Number(slot.request_past_min_notice) === 1) {
    return 'Customers see request to book on the booking site.'
  }
  return 'Customers see call to book on the booking site.'
}

function countdownNotice(remainingMs: number, slot: SlotNoticeInput): SlotNotice {
  const remaining = countdownRemaining(remainingMs)
  return {
    state: 'countdown',
    label: `closes in ${remaining}`,
    title: `Online booking closes in ${remaining}, then: ${customerEffect(slot)}`
  }
}

const STAFF_RECOVERY = 'Staff can still book this slot.'

export function classifySlotNotice(
  slot: SlotNoticeInput,
  anchor: NoticeAnchor | null,
  displayMode: NoticeDisplayMode
): SlotNotice | null {
  if (!slot || slot.status !== 'available' || slot.is_all_day) return null
  if (!slot.booking_date || !slot.start_time) return null

  const startMs = wallClockMs(slot.booking_date, slot.start_time)
  if (isNaN(startMs)) return null

  const now = getVenueNowMs(anchor)
  const offset = offsetMs(slot.min_booking_period, slot.min_booking_value)

  if (Number(slot.allow_late_booking) === 1) {
    // Late booking mode: bookable online until start + offset, then closed
    // until the slot ends.
    const cutoff = startMs + offset
    let endMs = slot.end_time
      ? wallClockMs(slot.end_date || slot.booking_date, slot.end_time)
      : startMs
    if (isNaN(endMs)) endMs = startMs
    if (now > cutoff && now < endMs) {
      return {
        state: 'closed',
        label: 'online booking closed',
        title: `The late booking window has passed. ${STAFF_RECOVERY}`
      }
    }
    if (displayMode === 'countdown' && now <= cutoff && cutoff - now <= COUNTDOWN_HORIZON_MS) {
      return countdownNotice(cutoff - now, slot)
    }
    return null
  }

  // Minimum notice mode: online booking closes at start - offset. Once the
  // slot has started the state is self-evident, so no label.
  const cutoff = startMs - offset
  if (now >= cutoff && now < startMs) {
    let label = 'call to book only'
    if (Number(slot.hide_past_min_notice) === 1) {
      label = 'hidden from customers'
    } else if (Number(slot.request_past_min_notice) === 1) {
      label = 'request to book only'
    }
    return {
      state: 'closed',
      label,
      title: `${customerEffect(slot)} ${STAFF_RECOVERY}`
    }
  }
  if (displayMode === 'countdown' && now < cutoff && cutoff - now <= COUNTDOWN_HORIZON_MS) {
    return countdownNotice(cutoff - now, slot)
  }
  return null
}
